<template>
  <div class="contain">
    <el-alert
      class="dm-hint"
      type="warning"
      :closable="false"
      show-icon
      :title="
        $t(
          '운영자 도구입니다. 행을 넣으면 그 필드의 배달앱 반영이 서버 배포 없이 열립니다 — 진짜 원천은 룰 배포판(omni-channel-ai)입니다.'
        )
      ">
    </el-alert>

    <el-row :gutter="16">
      <el-col :md="12">
        <el-card shadow="never">
          <div slot="header">{{ $t('지원 선언 등록') }}</div>
          <el-form :model="form" :label-position="$device.mobile ? `top` : `left`" label-width="140px" :size="size">
            <el-form-item :label="$t('플랫폼')">
              <el-select v-model="form.platformGroup" style="width: 100%">
                <el-option v-for="p in platforms" :key="p" :label="platformName(p)" :value="p"></el-option>
              </el-select>
            </el-form-item>
            <el-form-item :label="$t('실행 표면')">
              <el-select v-model="form.executorKind" style="width: 100%">
                <el-option v-for="k in vocab.executorKinds" :key="k" :label="k" :value="k"></el-option>
              </el-select>
            </el-form-item>
            <el-form-item :label="$t('룰 ID')">
              <el-input v-model="form.ruleId" maxlength="60" show-word-limit></el-input>
            </el-form-item>
            <el-form-item :label="$t('룰 버전')">
              <el-input v-model="form.ruleVersion" maxlength="40" show-word-limit></el-input>
              <div class="dm-weak">
                {{
                  $t('플랫폼·실행 표면·오퍼레이션·필드가 같으면 한 행입니다 — 버전을 바꿔 등록하면 그 행이 갱신됩니다.')
                }}
              </div>
            </el-form-item>
            <el-form-item :label="$t('오퍼레이션')">
              <el-select v-model="form.operation" style="width: 100%" @change="onOperationChange">
                <el-option v-for="o in vocab.operations" :key="o" :label="o" :value="o"></el-option>
              </el-select>
            </el-form-item>
            <el-form-item :label="$t('필드')">
              <el-select v-model="form.field" style="width: 100%" :disabled="fieldFolded">
                <el-option v-for="f in vocab.fields" :key="f" :label="fieldLabel(f)" :value="f"></el-option>
              </el-select>
              <div v-if="fieldFolded" class="dm-weak">
                {{ $t('상태 오퍼레이션은 서버가 필드를 * 로 접습니다.') }}
              </div>
            </el-form-item>
            <el-form-item :label="$t('범위')">
              <el-select v-model="form.scopeTp" style="width: 100%">
                <el-option v-for="s in vocab.scopeTps" :key="s" :label="s" :value="s"></el-option>
              </el-select>
            </el-form-item>

            <el-divider content-position="left">{{ $t('선택 축 — 건드린 것만 보냅니다') }}</el-divider>

            <el-form-item :label="$t('보이는 화면 필수')">
              <el-checkbox v-model="touchVisible">{{ $t('이 축을 변경') }}</el-checkbox>
              <el-radio-group v-model="form.requiresVisibleYn" :disabled="!touchVisible" size="mini" class="dm-yn">
                <el-radio-button label="Y">Y</el-radio-button>
                <el-radio-button label="N">N</el-radio-button>
              </el-radio-group>
            </el-form-item>
            <el-form-item :label="$t('활성')">
              <el-checkbox v-model="touchActive">{{ $t('이 축을 변경') }}</el-checkbox>
              <el-radio-group v-model="form.activeYn" :disabled="!touchActive" size="mini" class="dm-yn">
                <el-radio-button label="Y">Y</el-radio-button>
                <el-radio-button label="N">N</el-radio-button>
              </el-radio-group>
            </el-form-item>

            <el-alert
              v-if="invisibleWarning"
              class="dm-hint"
              type="warning"
              :closable="false"
              show-icon
              :title="invisibleWarning"></el-alert>

            <el-form-item>
              <el-button type="primary" :size="size" :loading="saving" @click="submit">{{ $t('등록') }}</el-button>
            </el-form-item>
          </el-form>
        </el-card>
      </el-col>

      <el-col :md="12">
        <el-card shadow="never">
          <div slot="header">{{ $t('이 화면에서 등록한 결과') }}</div>
          <el-alert
            class="dm-hint"
            type="info"
            :closable="false"
            :title="
              $t('조회 API가 없어 전체 목록은 볼 수 없습니다. 아래는 이번 화면에서 등록한 것만입니다.')
            "></el-alert>
          <el-table :data="registered" size="mini" border :empty-text="$t('등록한 선언이 없습니다.')">
            <el-table-column :label="$t('플랫폼')" width="90">
              <template slot-scope="s">{{ platformName(s.row.platformGroup) }}</template>
            </el-table-column>
            <el-table-column :label="$t('룰')" min-width="160" show-overflow-tooltip>
              <template slot-scope="s">{{ s.row.ruleId }} · {{ s.row.ruleVersion }}</template>
            </el-table-column>
            <el-table-column :label="$t('오퍼레이션')" width="100" prop="operation"></el-table-column>
            <el-table-column :label="$t('필드')" width="100" prop="field"></el-table-column>
            <el-table-column :label="$t('범위')" width="90" prop="scopeTp"></el-table-column>
            <el-table-column :label="$t('보임')" width="70" align="center" prop="requiresVisibleYn"></el-table-column>
            <el-table-column :label="$t('활성')" width="70" align="center" prop="activeYn"></el-table-column>
            <el-table-column :label="$t('결과')" width="90" align="center">
              <template slot-scope="s">
                <el-tag :type="s.row.created ? 'success' : 'info'" size="mini">
                  {{ s.row.created ? $t('신규') : $t('갱신') }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import mainVueMixin from '@/mixins/mainVueMixin'
import { initData } from '@/utils/constants'
import {
  CAPABILITY_VOCAB,
  DELIVERY_CAPABILITY_API,
  DELIVERY_MENU_PLATFORMS,
  capabilityFoldsField,
  deliveryFieldLabel,
  deliveryMenuErrorMessage,
  deliveryMenuPlatformName,
} from '@/utils/deliveryMenuSync'

/**
 * ⑤ capability 등록 — `POST /api/admin/delivery-capabilities` (admin·mng 전용).
 *
 * 경로 접두사가 `/api/admin/**` 로 달라 화면을 분리한다 — 인가 판정이 URL 패턴 + METHOD 단위라
 * 같은 `POST /api/delivery-menu/**` 아래 두면 매장 계정이 planner 를 지배하는 capability 를 스스로
 * 켤 수 있다 (§9.2 · §12 · §14 완료 조건).
 *
 * **폼은 명시적으로 건드린 축만 보낸다** (§16.87·§16.99). `null` 은 "끄라" 가 아니라 "이 축은
 * 건드리지 않는다" 이다 — `activeYn` 만 토글하는 요청이 `requiresVisibleYn` 을 함께 실어 보내면
 * §12 실행 표면 제약이 경고 없이 풀린다.
 *
 * ★★ **두 축의 안전한 쪽은 반대다** (§16.245⑦). `requiresVisibleYn='Y'` 는 제약을 **거는** 값이라
 * 서버 기본값이 `'Y'` 이지만, `activeYn='Y'` 는 **바깥 배달앱으로 나가는 쓰기를 여는** 값이라
 * 서버 기본값이 `'N'` 이다. 초판은 둘을 "컬럼 기본값은 안전한 쪽 `'Y'`" 로 묶어 적었고, 그 독해가
 * 등록 API 로 만든 신규 행을 오래 살아 있게 했다. 개통은 언제나 명시로 한다.
 */
export default {
  name: 'DeliveryCapabilityView',
  mixins: [mainVueMixin],

  data() {
    const data = initData()
    data.form = {
      platformGroup: DELIVERY_MENU_PLATFORMS[0],
      executorKind: CAPABILITY_VOCAB.executorKinds[0],
      ruleId: '',
      ruleVersion: '',
      operation: 'UPDATE',
      field: 'smenuNm',
      scopeTp: 'SHOP',
      requiresVisibleYn: 'Y',
      // 서버가 신규 행에 넣는 값과 같게 둔다 — 안 건드리면 보내지 않으므로 이 값은 **표시**이고,
      // 표시가 서버와 다르면 운영자가 「Y 로 만들어졌다」고 읽는다(배달앱 쓰기가 열렸다는 뜻이다).
      activeYn: 'N',
    }
    data.touchVisible = false
    data.touchActive = false
    data.registered = []
    data.saving = false
    return data
  },

  computed: {
    vocab() {
      return CAPABILITY_VOCAB
    },
    platforms() {
      return DELIVERY_MENU_PLATFORMS
    },
    fieldFolded() {
      return capabilityFoldsField(this.form.operation)
    },
    /**
     * `requiresVisibleYn='Y'` 인데 보이지 않는 표면이면 planner 가 그 선언을 해석하지 않는다.
     * 서버는 거절하지 않고 기록만 하므로 "저장됨 = 동작함" 으로 읽히지 않게 여기서 알린다 (§12).
     */
    invisibleWarning() {
      const visible = CAPABILITY_VOCAB.visibleExecutorKinds.indexOf(this.form.executorKind) >= 0
      const requiresVisible = !this.touchVisible || this.form.requiresVisibleYn === 'Y'
      if (visible || !requiresVisible) return null
      return this.$t(
        '보이는 화면이 필수인 선언인데 실행 표면이 보이지 않는 종류입니다. 등록은 되지만 planner 가 이 선언을 쓰지 않습니다.'
      )
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.mainVue.currVue = this
    })
  },

  methods: {
    platformName: deliveryMenuPlatformName,
    fieldLabel: deliveryFieldLabel,

    onOperationChange() {
      // 서버가 `*` 로 접는 축이라 폼 값이 반영된다고 믿게 두지 않는다 (§4.6)
      if (this.fieldFolded) this.form.field = 'smenuNm'
    },

    payload() {
      const body = {
        platformGroup: this.form.platformGroup,
        executorKind: this.form.executorKind,
        ruleId: this.form.ruleId.trim(),
        ruleVersion: this.form.ruleVersion.trim(),
        operation: this.form.operation,
        scopeTp: this.form.scopeTp,
      }
      if (!this.fieldFolded) body.field = this.form.field
      if (this.touchVisible) body.requiresVisibleYn = this.form.requiresVisibleYn
      if (this.touchActive) body.activeYn = this.form.activeYn
      return body
    },

    async submit() {
      this.saving = true
      try {
        const res = await this.$axios.post(DELIVERY_CAPABILITY_API, this.payload())
        const view = res.data
        this.registered.unshift(view)
        this.$message({
          type: 'success',
          message: view.created ? this.$t('새 지원 선언을 추가했습니다.') : this.$t('기존 선언을 갱신했습니다.'),
        })
      } catch (e) {
        // 409 는 동시 등록 충돌 — 재조회 후 재시도 안내로 충분하다
        this.$message({ type: 'error', message: deliveryMenuErrorMessage(e), duration: 6000 })
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.dm-hint {
  margin-bottom: 12px;
}
.dm-weak {
  color: #909399;
  font-size: 12px;
}
.dm-yn {
  margin-left: 12px;
}
</style>
